// Modified BlogNewUiPage.jsx

import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Http from "../../Http";
import { Blog_Api } from "../../Helper";

const BlogNewUiPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [blogs, setBlogs] = useState([]);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [latestBlogs, setLatestBlogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [error, setError] = useState(null);

  // Extract category from URL params on initial load
  useEffect(() => {
    const categoryParam = searchParams.get("category");
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams]);

  // Fetch blogs based on page and category
  const fetchBlogs = async (pageNumber = 1, category = null) => {
    try {
      setLoading(true);
      setError(null);
      
      let url = `${Blog_Api}?page=${pageNumber}`;
      if (category) {
        url += `&category=${encodeURIComponent(category)}`;
      }
      
      const data = await Http.get(url);
      
      if (data && data.blogs) {
        setBlogs(data.blogs);
        setPage(data.page || 1);
        setTotalPages(data.total_pages || 1);
      } else {
        setBlogs([]);
        console.warn("No blogs data received from API");
      }

      // Always fetch recent and latest blogs regardless of pagination
      if (pageNumber === 1 || recentBlogs.length === 0 || latestBlogs.length === 0) {
        const allBlogsData = await Http.get(`${Blog_Api}?page=1`);
        
        if (allBlogsData && allBlogsData.blogs) {
          const allBlogs = allBlogsData.blogs || [];
          setRecentBlogs(allBlogs.length >= 5 ? allBlogs.slice(5, 10) : allBlogs.slice(0, 5));
          setLatestBlogs(allBlogs.length >= 4 ? allBlogs.slice(0, 4) : allBlogs);
        }
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
      setError("Failed to load blogs. Please try again later.");
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch blogs when page or category changes
  useEffect(() => {
    fetchBlogs(page, selectedCategory);
  }, [page, selectedCategory]);

  const handleNext = () => {
    if (page < totalPages) {
      setPage(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrev = () => {
    if (page > 1) {
      setPage(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setPage(1); // Reset to first page when changing category
    
    // Update URL params
    setSearchParams({ category });
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearCategory = () => {
    setSelectedCategory(null);
    setPage(1);
    
    // Clear URL params
    setSearchParams({});
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Format date for better display with error handling
  const formatDate = (dateString) => {
    if (!dateString) return "No date";
    
    try {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Date formatting error:", error);
      return dateString; // Return original string if formatting fails
    }
  };

  // Helper function to safely get category
  const safeCategory = (blog) => {
    return blog && blog.content_category ? blog.content_category.trim() : "Uncategorized";
  };

  return (
    <div className="max-w-7xl mx-auto p-4">
      {/* Category filter indicator */}
      {selectedCategory && (
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 mt-16 p-3 bg-blue-50 rounded-lg shadow-sm">
          <div className="text-lg mb-2 md:mb-0 text-center md:text-left">
            Showing blogs in category: <span className="font-bold text-blue-700">{selectedCategory}</span>
          </div>
          <button 
            onClick={handleClearCategory}
            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm transition-colors duration-300"
          >
            Clear Filter
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
        {/* Main Blogs */}
        <div className="lg:col-span-2">
          <h1 className="text-2xl md:text-3xl font-bold mb-6 border-b-2 border-blue-500 pb-2 inline-block">
            {selectedCategory ? `${selectedCategory} Blogs` : 'All Blogs'}
          </h1>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              <p className="mt-2">Loading...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg p-8">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xl font-semibold text-red-500">{error}</p>
              <button 
                onClick={() => fetchBlogs(1, selectedCategory)}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-300"
              >
                Try Again
              </button>
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg p-8">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xl font-semibold">No blogs found for this category</p>
              <p className="mt-2">Please try another category or clear filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {blogs.map((blog) => (
                <div key={blog.id || `blog-${blog.content_url}`} className="border rounded-xl shadow-md p-4 hover:shadow-lg transition-shadow duration-300 flex flex-col h-full bg-white">
                  <div className="overflow-hidden rounded-md mb-4">
                  <Link 
                  to={`/blog/${blog.content_url}`}>
                    <img
                      src={blog.content_image || "/api/placeholder/400/300"}
                      alt={blog.content_title || "Blog image"}
                      className="h-48 w-full object-cover hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/api/placeholder/400/300";
                      }}
                    />
                    </Link>
                  </div>
                  <div className="flex items-center mb-2 justify-between">
                    {/* Only display category name without additional styling */}
                    <span className="text-base text-blue-500">
                      {safeCategory(blog)}
                    </span>
                    <span className="text-xs text-gray-500">{formatDate(blog.content_date)}</span>
                  </div>
                  <h2 className="text-xl font-semibold mb-2 line-clamp-2 hover:text-blue-600 transition-colors">
                    {blog.content_title || "Untitled Blog"}
                  </h2>
                  <p
                    className="text-gray-600 mb-4 flex-grow line-clamp-3"
                    dangerouslySetInnerHTML={{
                      __html: blog?.content_description
                        ? `${blog.content_description.substring(0, 120)}${blog.content_description.length > 120 ? '...' : ''}`
                        : 'No description available'
                    }}
                  />
                  <Link 
                    to={`/blog/${blog.content_url}`} 
                    className="mt-auto px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-300 w-full text-center"
                  >
                    Read More
                  </Link>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && !error && blogs.length > 0 && (
            <div className="flex justify-center items-center mt-8 gap-4">
              <button
                onClick={handlePrev}
                disabled={page === 1}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>
              <span className="text-lg font-semibold">{page} / {totalPages}</span>
              <button
                onClick={handleNext}
                disabled={page === totalPages || totalPages === 0}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 flex items-center"
              >
                Next
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Sidebar - Categories section removed */}
        <div className="mt-8 lg:mt-0">
          {/* Latest Blogs */}
          <div className="bg-gray-50 p-4 rounded-lg shadow-md mb-6">
            <h2 className="text-xl font-bold mb-4 border-b-2 border-blue-500 pb-2">Latest Blogs</h2>
            {latestBlogs.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 mr-2"></div>
                <span>Loading latest blogs...</span>
              </div>
            ) : (
              <div className="space-y-3">
                {latestBlogs.map(blog => (
                  <Link 
                    to={`/blog/${blog.content_url}`} 
                    key={blog.id || `latest-${blog.content_url}`} 
                    className="block border-b pb-3 hover:bg-gray-100 p-2 cursor-pointer rounded-md transition-colors duration-300"
                  >
                    <div className="flex items-start">
                      <img 
                        src={blog.content_image || "/api/placeholder/400/300"} 
                        alt={blog.content_title || "Blog image"} 
                        className="w-16 h-16 object-cover rounded-md mr-2"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/api/placeholder/400/300";
                        }}
                      />
                      <div>
                        <h3 className="text-md font-semibold line-clamp-2 hover:text-blue-600 transition-colors">
                          {blog.content_title || "Untitled Blog"}
                        </h3>
                        <div className="flex justify-between mt-1">
                          {/* Simple text for category */}
                          <span className="text-base text-blue-500">
                            {safeCategory(blog)}
                          </span>
                          <p className="text-gray-500 text-xs">{formatDate(blog.content_date)}</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
          
          {/* Recent Blogs */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6 shadow-md">
            <h2 className="text-xl font-bold mb-4 border-b-2 border-blue-500 pb-2">Recent Blogs</h2>
            {recentBlogs.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 mr-2"></div>
                <span>Loading recent blogs...</span>
              </div>
            ) : (
              <div className="space-y-3">
                {recentBlogs.map(blog => (
                  <Link 
                    to={`/blog/${blog.content_url}`} 
                    key={blog.id || `recent-${blog.content_url}`} 
                    className="block border-b pb-3 hover:bg-gray-100 p-2 cursor-pointer rounded-md transition-colors duration-300"
                  >
                    <h3 className="text-md font-semibold line-clamp-2 hover:text-blue-600 transition-colors">
                      {blog.content_title || "Untitled Blog"}
                    </h3>
                    <div className="flex justify-between mt-1">
                      {/* Simple text for category */}
                      <span className="text-base text-blue-500">
                        {safeCategory(blog)}
                      </span>
                      <p className="text-gray-500 text-xs">{formatDate(blog.content_date)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogNewUiPage;

// import React, { useEffect, useState } from "react";
// import { Link, useSearchParams } from "react-router-dom";
// import Http from "../../Http";
// import { Blog_Api } from "../../Helper";

// const BlogNewUiPage = () => {
//   const [searchParams, setSearchParams] = useSearchParams();
//   const [blogs, setBlogs] = useState([]);
//   const [recentBlogs, setRecentBlogs] = useState([]);
//   const [latestBlogs, setLatestBlogs] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [page, setPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);
//   const [loading, setLoading] = useState(false);
//   const [selectedCategory, setSelectedCategory] = useState(null);
//   const [error, setError] = useState(null);

//   // Extract category from URL params on initial load
//   useEffect(() => {
//     const categoryParam = searchParams.get("category");
//     if (categoryParam) {
//       setSelectedCategory(categoryParam);
//     }
//   }, [searchParams]);

//   // Fetch all categories once on component mount
//   // This ensures categories remain the same regardless of pagination
//   useEffect(() => {
//     const fetchAllCategories = async () => {
//       try {
//         const allCategoriesData = await Http.get(`${Blog_Api}?page=1&limit=50`);
        
//         if (allCategoriesData && allCategoriesData.blogs) {
//           // Safely extract categories, handling potential nulls
//           const uniqueCategories = [...new Set(
//             allCategoriesData.blogs
//               .filter(blog => blog && blog.content_category) // Filter out null/undefined categories
//               .map(blog => blog.content_category.trim())
//           )];
//           setCategories(uniqueCategories);
//         }
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//       }
//     };

//     fetchAllCategories();
//   }, []);

//   // Fetch blogs based on page and category
//   const fetchBlogs = async (pageNumber = 1, category = null) => {
//     try {
//       setLoading(true);
//       setError(null);
      
//       let url = `${Blog_Api}?page=${pageNumber}`;
//       if (category) {
//         url += `&category=${encodeURIComponent(category)}`;
//       }
      
//       const data = await Http.get(url);
      
//       if (data && data.blogs) {
//         setBlogs(data.blogs);
//         setPage(data.page || 1);
//         setTotalPages(data.total_pages || 1);
//       } else {
//         setBlogs([]);
//         console.warn("No blogs data received from API");
//       }

//       // Always fetch recent and latest blogs regardless of pagination
//       if (pageNumber === 1 || recentBlogs.length === 0 || latestBlogs.length === 0) {
//         const allBlogsData = await Http.get(`${Blog_Api}?page=1`);
        
//         if (allBlogsData && allBlogsData.blogs) {
//           const allBlogs = allBlogsData.blogs || [];
//           setRecentBlogs(allBlogs.length >= 5 ? allBlogs.slice(5, 10) : allBlogs.slice(0, 5));
//           setLatestBlogs(allBlogs.length >= 4 ? allBlogs.slice(0, 4) : allBlogs);
//         }
//       }
//     } catch (error) {
//       console.error("Error fetching blogs:", error);
//       setError("Failed to load blogs. Please try again later.");
//       setBlogs([]);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Re-fetch blogs when page or category changes
//   useEffect(() => {
//     fetchBlogs(page, selectedCategory);
//   }, [page, selectedCategory]);

//   const handleNext = () => {
//     if (page < totalPages) {
//       setPage(prev => prev + 1);
//       window.scrollTo({ top: 0, behavior: 'smooth' });
//     }
//   };

//   const handlePrev = () => {
//     if (page > 1) {
//       setPage(prev => prev - 1);
//       window.scrollTo({ top: 0, behavior: 'smooth' });
//     }
//   };

//   const handleCategoryClick = (category) => {
//     setSelectedCategory(category);
//     setPage(1); // Reset to first page when changing category
    
//     // Update URL params
//     setSearchParams({ category });
    
//     window.scrollTo({ top: 0, behavior: 'smooth' });
//   };

//   const handleClearCategory = () => {
//     setSelectedCategory(null);
//     setPage(1);
    
//     // Clear URL params
//     setSearchParams({});
    
//     window.scrollTo({ top: 0, behavior: 'smooth' });
//   };

//   // Format date for better display with error handling
//   const formatDate = (dateString) => {
//     if (!dateString) return "No date";
    
//     try {
//       const options = { year: 'numeric', month: 'short', day: 'numeric' };
//       return new Date(dateString).toLocaleDateString(undefined, options);
//     } catch (error) {
//       console.error("Date formatting error:", error);
//       return dateString; // Return original string if formatting fails
//     }
//   };

//   // Helper function to safely get category
//   const safeCategory = (blog) => {
//     return blog && blog.content_category ? blog.content_category.trim() : "Uncategorized";
//   };

//   return (
//     <div className="max-w-7xl mx-auto p-4">
//       {/* Category filter indicator */}
//       {selectedCategory && (
//         <div className="flex flex-col md:flex-row justify-between items-center mb-6 mt-16 p-3 bg-blue-50 rounded-lg shadow-sm">
//           <div className="text-lg mb-2 md:mb-0 text-center md:text-left">
//             Showing blogs in category: <span className="font-bold text-blue-700">{selectedCategory}</span>
//           </div>
//           <button 
//             onClick={handleClearCategory}
//             className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm transition-colors duration-300"
//           >
//             Clear Filter
//           </button>
//         </div>
//       )}

//       <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
//         {/* Main Blogs */}
//         <div className="lg:col-span-2">
//           <h1 className="text-2xl md:text-3xl font-bold mb-6 border-b-2 border-blue-500 pb-2 inline-block">
//             {selectedCategory ? `${selectedCategory} Blogs` : 'All Blogs'}
//           </h1>

//           {loading ? (
//             <div className="text-center py-12">
//               <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
//               <p className="mt-2">Loading...</p>
//             </div>
//           ) : error ? (
//             <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg p-8">
//               <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
//               </svg>
//               <p className="text-xl font-semibold text-red-500">{error}</p>
//               <button 
//                 onClick={() => fetchBlogs(1, selectedCategory)}
//                 className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-300"
//               >
//                 Try Again
//               </button>
//             </div>
//           ) : blogs.length === 0 ? (
//             <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg p-8">
//               <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
//               </svg>
//               <p className="text-xl font-semibold">No blogs found for this category</p>
//               <p className="mt-2">Please try another category or clear filters</p>
//             </div>
//           ) : (
//             <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
//               {blogs.map((blog) => (
//                 <div key={blog.id || `blog-${blog.content_url}`} className="border rounded-xl shadow-md p-4 hover:shadow-lg transition-shadow duration-300 flex flex-col h-full bg-white">
//                   <div className="overflow-hidden rounded-md mb-4">
//                     <img
//                       src={blog.content_image || "/api/placeholder/400/300"}
//                       alt={blog.content_title || "Blog image"}
//                       className="h-48 w-full object-cover hover:scale-105 transition-transform duration-500"
//                       onError={(e) => {
//                         e.target.onerror = null;
//                         e.target.src = "/api/placeholder/400/300";
//                       }}
//                     />
//                   </div>
//                   <div className="flex items-center mb-2 justify-between">
//                     <span 
//                       className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full cursor-pointer hover:bg-blue-200 transition-colors"
//                       onClick={() => handleCategoryClick(safeCategory(blog))}
//                     >
//                       {safeCategory(blog)}
//                     </span>
//                     <span className="text-xs text-gray-500">{formatDate(blog.content_date)}</span>
//                   </div>
//                   <h2 className="text-xl font-semibold mb-2 line-clamp-2 hover:text-blue-600 transition-colors">
//                     {blog.content_title || "Untitled Blog"}
//                   </h2>
//                   <p
//                     className="text-gray-600 mb-4 flex-grow line-clamp-3"
//                     dangerouslySetInnerHTML={{
//                       __html: blog?.content_description
//                         ? `${blog.content_description.substring(0, 120)}${blog.content_description.length > 120 ? '...' : ''}`
//                         : 'No description available'
//                     }}
//                   />
//                   <Link 
//                     to={`/blog/${blog.content_url}`} 
//                     className="mt-auto px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-300 w-full text-center"
//                   >
//                     Read More
//                   </Link>
//                 </div>
//               ))}
//             </div>
//           )}

//           {/* Pagination */}
//           {!loading && !error && blogs.length > 0 && (
//             <div className="flex justify-center items-center mt-8 gap-4">
//               <button
//                 onClick={handlePrev}
//                 disabled={page === 1}
//                 className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 flex items-center"
//               >
//                 <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
//                 </svg>
//                 Previous
//               </button>
//               <span className="text-lg font-semibold">{page} / {totalPages}</span>
//               <button
//                 onClick={handleNext}
//                 disabled={page === totalPages || totalPages === 0}
//                 className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 flex items-center"
//               >
//                 Next
//                 <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
//                 </svg>
//               </button>
//             </div>
//           )}
//         </div>

//         {/* Sidebar */}
//         <div className="mt-8 lg:mt-0">
//           {/* Categories sidebar */}
//           <div className="bg-gray-50 p-4 rounded-lg mb-6 shadow-md">
//             <h2 className="text-xl font-bold mb-4 border-b-2 border-blue-500 pb-2">Categories</h2>
//             <div className="space-y-1">
//               {categories.length === 0 ? (
//                 <div className="text-center py-4 text-gray-500">
//                   <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 mr-2"></div>
//                   <span>Loading categories...</span>
//                 </div>
//               ) : (
//                 categories.map((category, index) => (
//                   <div 
//                     key={index}
//                     className={`p-2 cursor-pointer transition-colors duration-300 rounded-md flex items-center ${
//                       selectedCategory === category 
//                         ? "bg-blue-100 font-semibold text-blue-700" 
//                         : "hover:bg-gray-100"
//                     }`}
//                     onClick={() => handleCategoryClick(category)}
//                   >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
//                     </svg>
//                     {category}
//                   </div>
//                 ))
//               )}
//             </div>
//           </div>
//            {/* Latest Blogs */}
//            <div className="bg-gray-50 p-4 rounded-lg shadow-md mb-6">
//             <h2 className="text-xl font-bold mb-4 border-b-2 border-blue-500 pb-2">Latest Blogs</h2>
//             {latestBlogs.length === 0 ? (
//               <div className="text-center py-4 text-gray-500">
//                 <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 mr-2"></div>
//                 <span>Loading latest blogs...</span>
//               </div>
//             ) : (
//               <div className="space-y-3">
//                 {latestBlogs.map(blog => (
//                   <Link 
//                     to={`/blog/${blog.content_url}`} 
//                     key={blog.id || `latest-${blog.content_url}`} 
//                     className="block border-b pb-3 hover:bg-gray-100 p-2 cursor-pointer rounded-md transition-colors duration-300"
//                   >
//                     <div className="flex items-start">
//                       <img 
//                         src={blog.content_image || "/api/placeholder/400/300"} 
//                         alt={blog.content_title || "Blog image"} 
//                         className="w-16 h-16 object-cover rounded-md mr-2"
//                         onError={(e) => {
//                           e.target.onerror = null;
//                           e.target.src = "/api/placeholder/400/300";
//                         }}
//                       />
//                       <div>
//                         <h3 className="text-md font-semibold line-clamp-2 hover:text-blue-600 transition-colors">
//                           {blog.content_title || "Untitled Blog"}
//                         </h3>
//                         <div className="flex justify-between mt-1">
//                           <span 
//                             className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full cursor-pointer hover:bg-blue-200 transition-colors"
//                             onClick={(e) => {
//                               e.preventDefault();
//                               handleCategoryClick(safeCategory(blog));
//                             }}
//                           >
//                             {safeCategory(blog)}
//                           </span>
//                           <p className="text-gray-500 text-xs">{formatDate(blog.content_date)}</p>
//                         </div>
//                       </div>
//                     </div>
//                   </Link>
//                 ))}
//               </div>
//             )}
//           </div>
//           {/* Recent Blogs */}
//           <div className="bg-gray-50 p-4 rounded-lg mb-6 shadow-md">
//             <h2 className="text-xl font-bold mb-4 border-b-2 border-blue-500 pb-2">Recent Blogs</h2>
//             {recentBlogs.length === 0 ? (
//               <div className="text-center py-4 text-gray-500">
//                 <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 mr-2"></div>
//                 <span>Loading recent blogs...</span>
//               </div>
//             ) : (
//               <div className="space-y-3">
//                 {recentBlogs.map(blog => (
//                   <Link 
//                     to={`/blog/${blog.content_url}`} 
//                     key={blog.id || `recent-${blog.content_url}`} 
//                     className="block border-b pb-3 hover:bg-gray-100 p-2 cursor-pointer rounded-md transition-colors duration-300"
//                   >
//                     <h3 className="text-md font-semibold line-clamp-2 hover:text-blue-600 transition-colors">
//                       {blog.content_title || "Untitled Blog"}
//                     </h3>
//                     <div className="flex justify-between mt-1">
//                       <span 
//                         className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full cursor-pointer hover:bg-blue-200 transition-colors"
//                         onClick={(e) => {
//                           e.preventDefault();
//                           handleCategoryClick(safeCategory(blog));
//                         }}
//                       >
//                         {safeCategory(blog)}
//                       </span>
//                       <p className="text-gray-500 text-xs">{formatDate(blog.content_date)}</p>
//                     </div>
//                   </Link>
//                 ))}
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default BlogNewUiPage;


// import React, { useEffect, useState } from "react";
// import { Link, useSearchParams } from "react-router-dom";

// const BlogNewUiPage = () => {
//   const [searchParams, setSearchParams] = useSearchParams();
//   const [blogs, setBlogs] = useState([]);
//   const [recentBlogs, setRecentBlogs] = useState([]);
//   const [latestBlogs, setLatestBlogs] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [page, setPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);
//   const [loading, setLoading] = useState(false);
//   const [selectedCategory, setSelectedCategory] = useState(null);
//   const [error, setError] = useState(null);

//   // Extract category from URL params on initial load
//   useEffect(() => {
//     const categoryParam = searchParams.get("category");
//     if (categoryParam) {
//       setSelectedCategory(categoryParam);
//     }
//   }, [searchParams]);

//   const fetchBlogs = async (pageNumber = 1, category = null) => {
//     try {
//       setLoading(true);
//       setError(null);
      
//       let url = `https://test.thefinmaster.com/console/react-app-directory/test-blog-api.php?page=${pageNumber}`;
//       if (category) {
//         url += `&category=${encodeURIComponent(category)}`;
//       }
      
//       const response = await fetch(url);
      
//       if (!response.ok) {
//         throw new Error(`HTTP error! Status: ${response.status}`);
//       }
      
//       const data = await response.json();
      
//       if (data && data.blogs) {
//         // Make sure data.blogs and all required fields exist before setting state
//         setBlogs(data.blogs);
//         setPage(data.page || 1);
//         setTotalPages(data.total_pages || 1);
//       } else {
//         setBlogs([]);
//         console.warn("No blogs data received from API");
//       }

//       // Always fetch recent and latest blogs regardless of category filter
//       if (!category || pageNumber === 1) {
//         const allBlogsResponse = await fetch(`https://test.thefinmaster.com/console/react-app-directory/test-blog-api.php?page=1`);
        
//         if (!allBlogsResponse.ok) {
//           throw new Error(`HTTP error! Status: ${allBlogsResponse.status}`);
//         }
        
//         const allBlogsData = await allBlogsResponse.json();
        
//         if (allBlogsData && allBlogsData.blogs) {
//           // Safely select slices, checking length first
//           const allBlogs = allBlogsData.blogs || [];
//           setRecentBlogs(allBlogs.length >= 5 ? allBlogs.slice(5, 10) : allBlogs.slice(0, 5));
//           setLatestBlogs(allBlogs.length >= 4 ? allBlogs.slice(0, 4) : allBlogs);
//         }
//       }

//       // Extract unique categories from all blogs
//       if (!category || categories.length === 0) {
//         const allCategoriesResponse = await fetch(`https://test.thefinmaster.com/console/react-app-directory/test-blog-api.php?page=1&limit=50`);
        
//         if (!allCategoriesResponse.ok) {
//           throw new Error(`HTTP error! Status: ${allCategoriesResponse.status}`);
//         }
        
//         const allCategoriesData = await allCategoriesResponse.json();
        
//         if (allCategoriesData && allCategoriesData.blogs) {
//           // Safely extract categories, handling potential nulls
//           const uniqueCategories = [...new Set(
//             allCategoriesData.blogs
//               .filter(blog => blog && blog.content_category) // Filter out null/undefined categories
//               .map(blog => blog.content_category.trim())
//           )];
//           setCategories(uniqueCategories);
//         }
//       }
//     } catch (error) {
//       console.error("Error fetching blogs:", error);
//       setError("Failed to load blogs. Please try again later.");
//       setBlogs([]);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchBlogs(page, selectedCategory);
//   }, [page, selectedCategory]);

//   const handleNext = () => {
//     if (page < totalPages) {
//       setPage(prev => prev + 1);
//       window.scrollTo({ top: 0, behavior: 'smooth' });
//     }
//   };

//   const handlePrev = () => {
//     if (page > 1) {
//       setPage(prev => prev - 1);
//       window.scrollTo({ top: 0, behavior: 'smooth' });
//     }
//   };

//   const handleCategoryClick = (category) => {
//     setSelectedCategory(category);
//     setPage(1); // Reset to first page when changing category
    
//     // Update URL params
//     setSearchParams({ category });
    
//     window.scrollTo({ top: 0, behavior: 'smooth' });
//   };

//   const handleClearCategory = () => {
//     setSelectedCategory(null);
//     setPage(1);
    
//     // Clear URL params
//     setSearchParams({});
    
//     window.scrollTo({ top: 0, behavior: 'smooth' });
//   };

//   // Format date for better display with error handling
//   const formatDate = (dateString) => {
//     if (!dateString) return "No date";
    
//     try {
//       const options = { year: 'numeric', month: 'short', day: 'numeric' };
//       return new Date(dateString).toLocaleDateString(undefined, options);
//     } catch (error) {
//       console.error("Date formatting error:", error);
//       return dateString; // Return original string if formatting fails
//     }
//   };

//   // Helper function to safely get category
//   const safeCategory = (blog) => {
//     return blog && blog.content_category ? blog.content_category.trim() : "Uncategorized";
//   };

//   return (
//     <div className="max-w-7xl mx-auto p-4">
//       {/* Category filter indicator */}
//       {selectedCategory && (
//         <div className="flex flex-col md:flex-row justify-between items-center mb-6 mt-16 p-3 bg-blue-50 rounded-lg shadow-sm">
//           <div className="text-lg mb-2 md:mb-0 text-center md:text-left">
//             Showing blogs in category: <span className="font-bold text-blue-700">{selectedCategory}</span>
//           </div>
//           <button 
//             onClick={handleClearCategory}
//             className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm transition-colors duration-300"
//           >
//             Clear Filter
//           </button>
//         </div>
//       )}

//       <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
//         {/* Main Blogs */}
//         <div className="lg:col-span-2">
//           <h1 className="text-2xl md:text-3xl font-bold mb-6 border-b-2 border-blue-500 pb-2 inline-block">
//             {selectedCategory ? `${selectedCategory} Blogs` : 'All Blogs'}
//           </h1>

//           {loading ? (
//             <div className="text-center py-12">
//               <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
//               <p className="mt-2">Loading...</p>
//             </div>
//           ) : error ? (
//             <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg p-8">
//               <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
//               </svg>
//               <p className="text-xl font-semibold text-red-500">{error}</p>
//               <button 
//                 onClick={() => fetchBlogs(1, selectedCategory)}
//                 className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-300"
//               >
//                 Try Again
//               </button>
//             </div>
//           ) : blogs.length === 0 ? (
//             <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg p-8">
//               <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
//               </svg>
//               <p className="text-xl font-semibold">No blogs found for this category</p>
//               <p className="mt-2">Please try another category or clear filters</p>
//             </div>
//           ) : (
//             <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
//               {blogs.map((blog) => (
//                 <div key={blog.id || `blog-${blog.content_url}`} className="border rounded-xl shadow-md p-4 hover:shadow-lg transition-shadow duration-300 flex flex-col h-full bg-white">
//                   <div className="overflow-hidden rounded-md mb-4">
//                     <img
//                       src={blog.content_image || "/api/placeholder/400/300"}
//                       alt={blog.content_title || "Blog image"}
//                       className="h-48 w-full object-cover hover:scale-105 transition-transform duration-500"
//                       onError={(e) => {
//                         e.target.onerror = null;
//                         e.target.src = "/api/placeholder/400/300";
//                       }}
//                     />
//                   </div>
//                   <div className="flex items-center mb-2 justify-between">
//                     <span 
//                       className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full cursor-pointer hover:bg-blue-200 transition-colors"
//                       onClick={() => handleCategoryClick(safeCategory(blog))}
//                     >
//                       {safeCategory(blog)}
//                     </span>
//                     <span className="text-xs text-gray-500">{formatDate(blog.content_date)}</span>
//                   </div>
//                   <h2 className="text-xl font-semibold mb-2 line-clamp-2 hover:text-blue-600 transition-colors">
//                     {blog.content_title || "Untitled Blog"}
//                   </h2>
//                   <p
//                     className="text-gray-600 mb-4 flex-grow line-clamp-3"
//                     dangerouslySetInnerHTML={{
//                       __html: blog?.content_description
//                         ? `${blog.content_description.substring(0, 120)}${blog.content_description.length > 120 ? '...' : ''}`
//                         : 'No description available'
//                     }}
//                   />
//                   <Link 
//                     to={`/blog/${blog.content_url}`} 
//                     className="mt-auto px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-300 w-full text-center"
//                   >
//                     Read More
//                   </Link>
//                 </div>
//               ))}
//             </div>
//           )}

//           {/* Pagination */}
//           {!loading && !error && blogs.length > 0 && (
//             <div className="flex justify-center items-center mt-8 gap-4">
//               <button
//                 onClick={handlePrev}
//                 disabled={page === 1}
//                 className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 flex items-center"
//               >
//                 <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
//                 </svg>
//                 Previous
//               </button>
//               <span className="text-lg font-semibold">{page} / {totalPages}</span>
//               <button
//                 onClick={handleNext}
//                 disabled={page === totalPages || totalPages === 0}
//                 className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 flex items-center"
//               >
//                 Next
//                 <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
//                 </svg>
//               </button>
//             </div>
//           )}
//         </div>

//         {/* Sidebar */}
//         <div className="mt-8 lg:mt-0">
//           {/* Categories sidebar */}
//           <div className="bg-gray-50 p-4 rounded-lg mb-6 shadow-md">
//             <h2 className="text-xl font-bold mb-4 border-b-2 border-blue-500 pb-2">Categories</h2>
//             <div className="space-y-1">
//               {categories.length === 0 ? (
//                 <div className="text-center py-4 text-gray-500">
//                   <p>Loading categories...</p>
//                 </div>
//               ) : (
//                 categories.map((category, index) => (
//                   <div 
//                     key={index}
//                     className={`p-2 cursor-pointer transition-colors duration-300 rounded-md flex items-center ${
//                       selectedCategory === category 
//                         ? "bg-blue-100 font-semibold text-blue-700" 
//                         : "hover:bg-gray-100"
//                     }`}
//                     onClick={() => handleCategoryClick(category)}
//                   >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
//                     </svg>
//                     {category}
//                   </div>
//                 ))
//               )}
//             </div>
//           </div>
//            {/* Latest Blogs */}
//            <div className="bg-gray-50 p-4 rounded-lg shadow-md">
//             <h2 className="text-xl font-bold mb-4 border-b-2 border-blue-500 pb-2">Latest Blogs</h2>
//             {latestBlogs.length === 0 ? (
//               <div className="text-center py-4 text-gray-500">
//                 <p>No latest blogs found</p>
//               </div>
//             ) : (
//               <div className="space-y-3">
//                 {latestBlogs.map(blog => (
//                   <Link 
//                     to={`/blog/${blog.content_url}`} 
//                     key={blog.id || `latest-${blog.content_url}`} 
//                     className="block border-b pb-3 hover:bg-gray-100 p-2 cursor-pointer rounded-md transition-colors duration-300"
//                   >
//                     <div className="flex items-start">
//                       <img 
//                         src={blog.content_image || "/api/placeholder/400/300"} 
//                         alt={blog.content_title || "Blog image"} 
//                         className="w-16 h-16 object-cover rounded-md mr-2"
//                         onError={(e) => {
//                           e.target.onerror = null;
//                           e.target.src = "/api/placeholder/400/300";
//                         }}
//                       />
//                       <div>
//                         <h3 className="text-md font-semibold line-clamp-2 hover:text-blue-600 transition-colors">
//                           {blog.content_title || "Untitled Blog"}
//                         </h3>
//                         <div className="flex justify-between mt-1">
//                           <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
//                             {safeCategory(blog)}
//                           </span>
//                           <p className="text-gray-500 text-xs">{formatDate(blog.content_date)}</p>
//                         </div>
//                       </div>
//                     </div>
//                   </Link>
//                 ))}
//               </div>
//             )}
//           </div>
//           {/* Recent Blogs */}
//           <div className="bg-gray-50 p-4 rounded-lg mb-6 shadow-md">
//             <h2 className="text-xl font-bold mb-4 border-b-2 border-blue-500 pb-2">Recent Blogs</h2>
//             {recentBlogs.length === 0 ? (
//               <div className="text-center py-4 text-gray-500">
//                 <p>No recent blogs found</p>
//               </div>
//             ) : (
//               <div className="space-y-3">
//                 {recentBlogs.map(blog => (
//                   <Link 
//                     to={`/blog/${blog.content_url}`} 
//                     key={blog.id || `recent-${blog.content_url}`} 
//                     className="block border-b pb-3 hover:bg-gray-100 p-2 cursor-pointer rounded-md transition-colors duration-300"
//                   >
//                     <h3 className="text-md font-semibold line-clamp-2 hover:text-blue-600 transition-colors">
//                       {blog.content_title || "Untitled Blog"}
//                     </h3>
//                     <div className="flex justify-between mt-1">
//                       <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
//                         {safeCategory(blog)}
//                       </span>
//                       <p className="text-gray-500 text-xs">{formatDate(blog.content_date)}</p>
//                     </div>
//                   </Link>
//                 ))}
//               </div>
//             )}
//           </div>

         
//         </div>
//       </div>
//     </div>
//   );
// };

// export default BlogNewUiPage;

